import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core'

import { TranslatePipe } from '@ngx-translate/core'
import { PrimeTemplate } from 'primeng/api'
import { Button } from 'primeng/button'
import { Dialog } from 'primeng/dialog'
import { InputText } from 'primeng/inputtext'

import { AlertCircleIconComponent } from '@ti/core/icons/alert-circle'
import { SuccessCircleIconComponent } from '@ti/core/icons/success-circle'

import { MfaClient } from '../../services/mfa.client'

@Component({
  selector: 'app-authenticator-code-dialog',
  templateUrl: 'authenticator-code-dialog.component.html',
  styleUrls: ['authenticator-code-dialog.component.scss'],
  imports: [
    AlertCircleIconComponent,
    Button,
    Dialog,
    InputText,
    PrimeTemplate,
    SuccessCircleIconComponent,
    TranslatePipe,
  ],
})
export class AuthenticatorCodeDialogComponent {
  @Output() cancelClicked = new EventEmitter<void>()
  @Output() confirmed = new EventEmitter<any>()

  @Input() mode: 'login' | 'setup' = 'login'

  protected readonly mfaClient = inject(MfaClient)

  protected readonly code = signal('')
  protected readonly isConfirmed = signal(false)
  protected readonly isError = signal(false)
  protected readonly isLoading = signal(false)
  protected readonly isOpened = signal(false)

  public open() {
    this.code.set('')
    this.isConfirmed.set(false)
    this.isOpened.set(true)
  }

  public close() {
    this.code.set('')
    this.isConfirmed.set(false)
    this.isError.set(false)
    this.isLoading.set(false)
    this.isOpened.set(false)
  }

  public cancel() {
    this.cancelClicked.emit()
    this.close()
  }

  public async verify() {
    const code = this.code()
    if (!code) {
      return
    }

    this.isLoading.set(true)
    try {
      if (this.mode === 'setup') {
        await this.mfaClient.verifyMfaSetup(code)
        this.isConfirmed.set(true)
      } else {
        const result = await this.mfaClient.confirmSignIn(code)
        this.close()
        this.confirmed.emit(result)
      }
    } catch (error) {
      this.isError.set(true)
      console.warn(`Cannot verify MFA code`, error)
    } finally {
      this.isLoading.set(false)
    }
  }

  public confirmSuccess() {
    this.close()
    this.confirmed.emit()
  }

  protected onCodeInput(event: Event) {
    const input = event.target as HTMLInputElement | null
    this.code.set(input?.value ?? '')
    this.isError.set(false)
  }
}
